import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  Checkbox,
  Input,
  Switch,
  message,
  Space,
  Table,
  Button,
  Popconfirm,
  Tag,
  Divider,
  Typography,
  Alert,
} from 'antd';
import {
  UserAddOutlined,
  DeleteOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  inviteCollaborator,
  getCollaborators,
  removeCollaborator,
  CollaborationRole,
  CollaborationPermission,
  Collaboration,
} from '../../../../services/collaboration';
import { getStaff, User } from '../../../../services/user';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

interface InviteCollaboratorModalProps {
  visible: boolean;
  caseId: number;
  caseData?: any; // Optional case data to filter out responsible/assistant lawyers
  onCancel: () => void;
  onSuccess: () => void;
}

const InviteCollaboratorModal: React.FC<InviteCollaboratorModalProps> = ({
  visible,
  caseId,
  caseData,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [staffUsers, setStaffUsers] = useState<User[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaboration[]>([]);
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  // Load staff users
  const loadStaffUsers = async () => {
    setLoadingStaff(true);
    try {
      const users = await getStaff({ limit: 1000 });
      setStaffUsers(users);
    } catch (error: any) {
      console.error('Failed to load staff users:', error);
      message.error('加载用户列表失败');
    } finally {
      setLoadingStaff(false);
    }
  };

  // Load current collaborators
  const loadCollaborators = async () => {
    setLoadingCollaborators(true);
    try {
      const data = await getCollaborators(caseId);
      setCollaborators(data);
    } catch (error: any) {
      console.error('Failed to load collaborators:', error);
      message.error('加载协作人员失败');
    } finally {
      setLoadingCollaborators(false);
    }
  };

  // Load data when modal opens
  useEffect(() => {
    if (visible) {
      loadStaffUsers();
      loadCollaborators();
    }
  }, [visible, caseId]);

  // Handle form submit
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Prepare invitation data
      const invitationData = {
        collaborator_ids: values.collaborator_ids,
        role: values.role,
        permissions: values.permissions,
        description: values.description,
        send_notification: values.send_notification || false,
      };

      await inviteCollaborator(caseId, invitationData);
      message.success('邀请协作人员成功');
      
      // Reload collaborators list
      await loadCollaborators();
      
      // Reset form
      form.resetFields();
      
      // Notify parent
      onSuccess();
    } catch (error: any) {
      console.error('Failed to invite collaborator:', error);
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error('邀请协作人员失败：' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle remove collaborator
  const handleRemove = async (userId: number) => {
    setRemovingId(userId);
    try {
      await removeCollaborator(caseId, userId);
      message.success('移除协作人员成功');
      
      // Reload collaborators list
      await loadCollaborators();
      
      // Notify parent
      onSuccess();
    } catch (error: any) {
      console.error('Failed to remove collaborator:', error);
      message.error('移除协作人员失败：' + (error.response?.data?.detail || error.message));
    } finally {
      setRemovingId(null);
    }
  };

  // Handle modal close
  const handleClose = () => {
    form.resetFields();
    onCancel();
  };

  // Get role text
  const getRoleText = (role: CollaborationRole): string => {
    const roleMap: Record<CollaborationRole, string> = {
      [CollaborationRole.CO_COUNSEL]: '协办律师',
      [CollaborationRole.CONSULTANT]: '顾问',
      [CollaborationRole.REVIEWER]: '审核人',
      [CollaborationRole.OBSERVER]: '观察者',
    };
    return roleMap[role] || role;
  };

  // Get role color
  const getRoleColor = (role: CollaborationRole): string => {
    const colorMap: Record<CollaborationRole, string> = {
      [CollaborationRole.CO_COUNSEL]: 'blue',
      [CollaborationRole.CONSULTANT]: 'green',
      [CollaborationRole.REVIEWER]: 'orange',
      [CollaborationRole.OBSERVER]: 'default',
    };
    return colorMap[role] || 'default';
  };

  // Get permission text
  const getPermissionText = (permission: CollaborationPermission): string => {
    const permissionMap: Record<CollaborationPermission, string> = {
      [CollaborationPermission.VIEW]: '查看',
      [CollaborationPermission.EDIT]: '编辑',
      [CollaborationPermission.MANAGE]: '管理',
    };
    return permissionMap[permission] || permission;
  };

  // Filter out already invited collaborators and responsible/assistant lawyers
  const getAvailableUsers = (): User[] => {
    const collaboratorIds = new Set(collaborators.map(c => c.collaborator_id));
    const excludedIds = new Set([
      ...Array.from(collaboratorIds),
      caseData?.responsible_lawyer_id,
      caseData?.assistant_lawyer_id,
    ].filter(Boolean));
    
    return staffUsers.filter(user => !excludedIds.has(user.id));
  };

  // Collaborators table columns
  const columns = [
    {
      title: '姓名',
      dataIndex: 'collaborator_name',
      key: 'collaborator_name',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: CollaborationRole) => (
        <Tag color={getRoleColor(role)}>{getRoleText(role)}</Tag>
      ),
    },
    {
      title: '权限',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (permissions: CollaborationPermission[]) => (
        <Space size="small">
          {permissions.map(permission => (
            <Tag key={permission}>{getPermissionText(permission)}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '邀请人',
      dataIndex: 'initiator_name',
      key: 'initiator_name',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Collaboration) => (
        <Popconfirm
          title="确定要移除此协作人员吗？"
          onConfirm={() => handleRemove(record.collaborator_id)}
          okText="确定"
          cancelText="取消"
        >
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            danger
            loading={removingId === record.collaborator_id}
          >
            移除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <TeamOutlined />
          <span>邀请协作人员</span>
        </Space>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={loading}
      okText="邀请"
      cancelText="取消"
      width={800}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Current Collaborators */}
        <div>
          <Text strong style={{ fontSize: 16, marginBottom: 12, display: 'block' }}>
            当前协作人员
          </Text>
          {loadingCollaborators ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              加载中...
            </div>
          ) : collaborators.length > 0 ? (
            <Table
              dataSource={collaborators}
              columns={columns}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ y: 200 }}
            />
          ) : (
            <Alert
              message="暂无协作人员"
              description="您可以在下方邀请律师协作处理此案件"
              type="info"
              showIcon
            />
          )}
        </div>

        <Divider />

        {/* Invitation Form */}
        <div>
          <Text strong style={{ fontSize: 16, marginBottom: 12, display: 'block' }}>
            <UserAddOutlined /> 邀请新的协作人员
          </Text>
          
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              role: CollaborationRole.CO_COUNSEL,
              permissions: [CollaborationPermission.VIEW],
              send_notification: true,
            }}
          >
            <Form.Item
              name="collaborator_ids"
              label="选择协作人员"
              rules={[
                { required: true, message: '请选择至少一位协作人员' },
              ]}
            >
              <Select
                mode="multiple"
                placeholder="请选择协作人员（可多选）"
                loading={loadingStaff}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={getAvailableUsers().map(user => ({
                  label: `${user.real_name || user.username}${user.department ? ` (${user.department})` : ''}`,
                  value: user.id,
                }))}
                maxTagCount="responsive"
              />
            </Form.Item>

            <Form.Item
              name="role"
              label="协作角色"
              rules={[{ required: true, message: '请选择协作角色' }]}
            >
              <Select placeholder="请选择协作角色">
                <Option value={CollaborationRole.CO_COUNSEL}>
                  协办律师 - 协助处理案件工作
                </Option>
                <Option value={CollaborationRole.CONSULTANT}>
                  顾问 - 提供专业咨询意见
                </Option>
                <Option value={CollaborationRole.REVIEWER}>
                  审核人 - 审核案件材料和进展
                </Option>
                <Option value={CollaborationRole.OBSERVER}>
                  观察者 - 仅查看案件信息
                </Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="permissions"
              label="协作权限"
              rules={[
                { required: true, message: '请选择至少一项权限' },
              ]}
            >
              <Checkbox.Group style={{ width: '100%' }}>
                <Space direction="vertical">
                  <Checkbox value={CollaborationPermission.VIEW}>
                    <Space direction="vertical" size={0}>
                      <Text strong>查看</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        可以查看案件的所有信息
                      </Text>
                    </Space>
                  </Checkbox>
                  <Checkbox value={CollaborationPermission.EDIT}>
                    <Space direction="vertical" size={0}>
                      <Text strong>编辑</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        可以编辑案件信息、添加跟进记录等
                      </Text>
                    </Space>
                  </Checkbox>
                  <Checkbox value={CollaborationPermission.MANAGE}>
                    <Space direction="vertical" size={0}>
                      <Text strong>管理</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        可以管理案件的所有方面，包括邀请其他协作人员
                      </Text>
                    </Space>
                  </Checkbox>
                </Space>
              </Checkbox.Group>
            </Form.Item>

            <Form.Item
              name="description"
              label="协作说明"
              tooltip="可选，说明邀请此人协作的原因或期望"
            >
              <TextArea
                rows={3}
                placeholder="请输入协作说明（可选）"
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item
              name="send_notification"
              label="通知设置"
              valuePropName="checked"
            >
              <Switch
                checkedChildren="发送通知"
                unCheckedChildren="不发送通知"
              />
            </Form.Item>

            <Alert
              message="提示"
              description="邀请协作人员后，他们将能够根据分配的权限访问和操作此案件。系统会自动记录协作邀请的大事记。"
              type="info"
              showIcon
              style={{ marginTop: 8 }}
            />
          </Form>
        </div>
      </Space>
    </Modal>
  );
};

export default InviteCollaboratorModal;
